/**
 * MessageList - Scrollable list of chat messages.
 *
 * Handles:
 * - Auto-scrolling to bottom on new messages
 * - Empty state with welcome screen
 * - Streaming indicator display
 * - Loading state
 */

import { useRef, useEffect } from 'react'
import { MessageBubble } from './MessageBubble'
import { WelcomeScreen } from './WelcomeScreen'
import { TransparencyPanel } from '../Transparency/TransparencyPanel'
import type { Message, ToolCall } from '@/store/chatStore'

interface MessageListProps {
  messages: Message[]
  isLoading: boolean
  streamingStatus: string | null
  streamingToolCalls: ToolCall[]
  onSuggestionClick: (text: string) => void
}

export function MessageList({
  messages,
  isLoading,
  streamingStatus,
  streamingToolCalls,
  onSuggestionClick,
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom on new messages or streaming updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingStatus, streamingToolCalls])

  if (messages.length === 0) {
    return <WelcomeScreen onSuggestionClick={onSuggestionClick} />
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          onActionComplete={(action, response) => {
            console.log('Action complete:', action.label, response)
          }}
        />
      ))}

      {/* Streaming/Transparency indicator */}
      {(isLoading || streamingStatus || streamingToolCalls.length > 0) && (
        <TransparencyPanel
          status={streamingStatus}
          toolCalls={streamingToolCalls}
          isLoading={isLoading}
        />
      )}

      <div ref={messagesEndRef} />
    </div>
  )
}

export default MessageList
